import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ErrorState } from "@arcadeai/design-system/components/ui/molecules/error-state";
import { captureException, withScope } from "@sentry/react";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { Home, RotateCcw } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect } from "react";
import { isAuthzDenial } from "@/lib/http-error";
import { getContextErrorKind } from "@/lib/network-error";
import { useHomeLink } from "@/lib/org-project-context/home-link";
import { errorDetails } from "./error-details";

export function CustomErrorComponent({
  error,
  info,
  reset,
}: ErrorComponentProps) {
  const router = useRouter();
  const { message, stack } = errorDetails(error);
  const posthog = usePostHog();
  const { reset: resetQueryErrors } = useQueryErrorResetBoundary();
  const homeLinkElement = useHomeLink();

  useEffect(() => {
    const errorKind = getContextErrorKind(error);
    withScope((scope) => {
      scope.setTag("errorBoundary", "tanstack-router");
      scope.setTag("errorKind", errorKind);
      scope.setLevel("error");
      scope.setContext("router", {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        timestamp: new Date().toISOString(),
      });
      if (info) {
        scope.setContext("info", info);
      }
      captureException(error);
    });
    // Sentry's beforeSend drops authorization denials; keep PostHog in step.
    if (isAuthzDenial(error)) {
      return;
    }
    // Pathname only, like pageviews: the query and hash can carry customer data.
    posthog.captureException(error, {
      $current_url: window.location.pathname,
      error_boundary: "tanstack-router",
      error_kind: errorKind,
    });
  }, [error, info, posthog]);

  // Without the query reset, a suspense query that already failed rethrows
  // its cached error on remount instead of fetching again.
  const handleTryAgain = useCallback(() => {
    resetQueryErrors();
    reset();
    try {
      router.invalidate();
    } catch {
      window.location.reload();
    }
  }, [reset, resetQueryErrors, router]);

  const stackTrace = [
    stack,
    info?.componentStack ? `\n\nComponent Stack:${info.componentStack}` : "",
  ]
    .filter(Boolean)
    .join("");

  return (
    <ErrorState
      errorMessage={message || "An unknown error occurred"}
      errorStack={stackTrace}
    >
      <Button className="flex-1" onClick={handleTryAgain} variant="outline">
        <RotateCcw className="mr-2 h-4 w-4" />
        Try Again
      </Button>
      <Button className="flex-1" render={homeLinkElement}>
        <Home className="mr-2 h-4 w-4" />
        Go Home
      </Button>
    </ErrorState>
  );
}
